import './App.css';
import { useCallback, useState, useEffect } from 'react';
import { AppRouter } from './app/router';
import { useUserContext } from '@root/providers/user_context';
import { useIdleTimeout } from '@root/hooks/useIdleTimeout';
import { appConfig } from './config/app.config';
import SessionTimeoutWarning from '@root/shared/components/SessionTimeoutWarning';

function App() {
  const { signOut, isLoggedIn } = useUserContext();
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(30); // 30 seconds grace period

  // Stage 1: Show warning at 14.5 minutes of inactivity
  const handleIdleWarning = useCallback(() => {
    setShowWarning(true);
    setRemainingSeconds(30); // Reset countdown to 30 seconds
  }, []);

  // Configure idle timeout to show warning at 14.5 minutes (15 min - 30 sec)
  const warningTimeMs = (appConfig.session.idleTimeoutMinutes * 60 * 1000) - (30 * 1000); // 14.5 minutes
  
  useIdleTimeout({
    timeout: warningTimeMs,
    onIdle: handleIdleWarning,
    enabled: isLoggedIn && !showWarning,
    events: ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'],
  });

  // Stage 2: Countdown timer for grace period
  useEffect(() => {
    if (!showWarning) return;

    const interval = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          signOut();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showWarning, signOut]);

  // Handle "Stay Logged In" button
  const handleStayLoggedIn = useCallback(() => {
    setShowWarning(false);
    // The idle timeout hook will automatically reset when user interacts
    // Trigger a synthetic event to reset the timer immediately
    window.dispatchEvent(new Event('mousedown'));
  }, []);

  // Handle immediate logout
  const handleLogoutNow = useCallback(() => {
    setShowWarning(false);
    signOut();
  }, [signOut]);

  return (
    <>
      <SessionTimeoutWarning
        open={showWarning}
        remainingSeconds={remainingSeconds}
        onStayLoggedIn={handleStayLoggedIn}
        onLogout={handleLogoutNow}
      />
      <div 
        data-testid="app"
        style={{
          width: '100%',
          height: '100vh',
          overflow: 'hidden'
        }}
      >
        <AppRouter />
      </div>
    </>
  )
}

export default App
